import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ShoppingBag, Zap, X, Globe, User, Fingerprint } from "lucide-react";

import ListTemplate from "../_templates/ListTemplate";
import BulkDeleteAction from "../../../components/common/BulkDeleteAction";
import QuickAddPlayersModal from "../../../components/players/QuickAddPlayersModal";
import { listPlayers, deletePlayer } from "../../../api/coredata.api";
import useNationalities from "../../../hooks/useNationalities";
import { playerDisplayName } from "../../../hooks/useNameMode";
import useAuthStore from "../../../store/auth.store";

const ROLE_TONE = {
  BATTER:       "bg-blue-500/15 text-blue-300 border-blue-400/30",
  BOWLER:       "bg-button-primary/20 text-button-primary border-button-primary/40",
  ALL_ROUNDER:  "bg-amber-500/15 text-amber-300 border-amber-400/30",
  WICKET_KEEPER:"bg-emerald-500/15 text-emerald-300 border-emerald-400/30",
};
const HAND_TONE = {
  RIGHT: "bg-bg-secondary text-text-primary border-border-default",
  LEFT:  "bg-button-primary/15 text-button-primary border-button-primary/30",
};
const Pill = ({ value, tone }) =>
  value ? (
    <span className={`inline-block text-[10px] uppercase tracking-wider rounded-full px-2 py-0.5 border ${tone || "bg-bg-secondary text-text-secondary border-border-default"}`}>
      {value === "ALL_ROUNDER" ? "ALLROUND" : value === "WICKET_KEEPER" ? "WK" : value}
    </span>
  ) : null;

// "Full name" used as a fallback line under the primary display name.
// The primary line (column "Player") uses playerDisplayName(r) — the
// nick_name when present. This composite is the formal first+middle+last
// shown beneath it only when distinct, so the operator can disambiguate
// two players with the same display name.
const fullName = (r) =>
  [r.first_name, r.middle_name, r.last_name].filter(Boolean).join(" ").trim() || "—";

export default function PlayerList() {
  const { byId, nameOf } = useNationalities();
  const isSuperuser = useAuthStore((s) => !!s.user?.is_superuser);
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [quickAddNonce, setQuickAddNonce] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const ownerTypeFilter = searchParams.get("owner_type") || "";
  const nationalityFilter = searchParams.get("nationality") || "";
  const genderFilter = searchParams.get("gender") || "";
  const hasCiIdFilter = searchParams.get("has_ci_id") || "";
  const allTenantsFilter = searchParams.get("all_tenants") || "";
  const ownerIdFilter = searchParams.get("owner_id") || "";
  const ciPlayerIdFilter = searchParams.get("ci_player_id") || "";
  const hasAnyFilter = ownerTypeFilter || nationalityFilter || genderFilter || hasCiIdFilter || allTenantsFilter || ownerIdFilter || ciPlayerIdFilter;
  const nationalityOptions = useMemo(
    () => Object.values(byId)
      .filter((n) => n?.name)
      .sort((a, b) => a.name.localeCompare(b.name)),
    [byId],
  );
  function setNationalityFilter(uuid) {
    const next = new URLSearchParams(searchParams);
    if (uuid) next.set("nationality", uuid);
    else next.delete("nationality");
    next.delete("page");
    setSearchParams(next, { replace: true });
    setQuickAddNonce((n) => n + 1);
  }
  function setGenderFilter(g) {
    const next = new URLSearchParams(searchParams);
    if (g) next.set("gender", g);
    else next.delete("gender");
    next.delete("page");
    setSearchParams(next, { replace: true });
    setQuickAddNonce((n) => n + 1);
  }
  // Cricinfo ID filter — exact match on Player.ci_player_id. Local
  // state holds the typed input; committed to the URL (and the API)
  // 300ms after typing stops so we don't fire a request per keystroke.
  const [ciPlayerIdInput, setCiPlayerIdInput] = useState(ciPlayerIdFilter);
  useEffect(() => {
    // Reset the input when the URL is cleared externally (e.g. Clear filters).
    setCiPlayerIdInput(ciPlayerIdFilter);
  }, [ciPlayerIdFilter]);
  useEffect(() => {
    const trimmed = ciPlayerIdInput.trim();
    if (trimmed === ciPlayerIdFilter) return;
    const t = setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      if (trimmed) next.set("ci_player_id", trimmed);
      else next.delete("ci_player_id");
      next.delete("page");
      setSearchParams(next, { replace: true });
      setQuickAddNonce((n) => n + 1);
    }, 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ciPlayerIdInput]);
  function clearAllFilters() {
    const next = new URLSearchParams(searchParams);
    next.delete("owner_type");
    next.delete("nationality");
    next.delete("gender");
    next.delete("has_ci_id");
    next.delete("all_tenants");
    next.delete("owner_id");
    next.delete("ci_player_id");
    setSearchParams(next, { replace: true });
    setQuickAddNonce((n) => n + 1); // force ListTemplate to re-fetch
  }
  return (
    <>
      {hasAnyFilter && (
        <div className="mb-3 rounded-md border border-emerald-500/40 bg-emerald-500/10 text-emerald-200 px-3 py-2 text-sm flex items-center gap-2 flex-wrap">
          <span className="font-semibold">Showing only:</span>
          {ownerTypeFilter && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40">
              owner_type = {ownerTypeFilter}
            </span>
          )}
          {nationalityFilter && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40">
              country = {nameOf(nationalityFilter)}
            </span>
          )}
          {genderFilter && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/20 border border-emerald-500/40">
              gender = {genderFilter}
            </span>
          )}
          {hasCiIdFilter === "true" && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200">
              ingested only (ING)
            </span>
          )}
          {allTenantsFilter === "true" && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-rose-500/20 border border-rose-500/40 text-rose-200">
              across all tenants (admin audit)
            </span>
          )}
          {ownerIdFilter && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-indigo-500/20 border border-indigo-500/40 text-indigo-200">
              owner_id = {ownerIdFilter.slice(0, 13)}…
            </span>
          )}
          {ciPlayerIdFilter && (
            <span className="font-mono text-[11px] px-1.5 py-0.5 rounded bg-amber-500/20 border border-amber-500/40 text-amber-200">
              cricinfo_id = {ciPlayerIdFilter}
            </span>
          )}
          <button
            onClick={clearAllFilters}
            className="ml-auto text-xs px-2 py-0.5 rounded border border-emerald-500/40 hover:bg-emerald-500/15 inline-flex items-center gap-1"
            title="Clear all filters"
          >
            <X className="w-3 h-3" /> Clear filters
          </button>
        </div>
      )}
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <Link
          to="/marketplace"
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-purple-500/10 border border-purple-400/40 text-purple-200 hover:bg-purple-500/15 transition"
        >
          <ShoppingBag className="w-3.5 h-3.5" />
          Need a full tournament squad fast? Browse marketplace packs →
        </Link>
        {/* T263 — Quick Add (bulk paste) for scorers at local grounds. */}
        <button
          type="button"
          onClick={() => setShowQuickAdd(true)}
          className="inline-flex items-center gap-2 text-xs px-3 py-1.5 rounded-full bg-amber-500/10 border border-amber-400/40 text-amber-200 hover:bg-amber-500/15 transition"
        >
          <Zap className="w-3.5 h-3.5" />
          Quick add 20+ players (paste names)
        </button>
        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-bg-secondary border border-border-default text-text-secondary">
          <Globe className="w-3.5 h-3.5" />
          <span>Country</span>
          <select
            value={nationalityFilter}
            onChange={(e) => setNationalityFilter(e.target.value)}
            className="bg-transparent text-text-primary text-xs focus:outline-none cursor-pointer"
          >
            <option value="" className="bg-bg-primary">All</option>
            {nationalityOptions.map((n) => (
              <option key={n.id} value={n.id} className="bg-bg-primary">
                {n.name}
              </option>
            ))}
          </select>
        </label>
        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-bg-secondary border border-border-default text-text-secondary">
          <User className="w-3.5 h-3.5" />
          <span>Gender</span>
          <select
            value={genderFilter}
            onChange={(e) => setGenderFilter(e.target.value)}
            className="bg-transparent text-text-primary text-xs focus:outline-none cursor-pointer"
          >
            <option value="" className="bg-bg-primary">All</option>
            <option value="MALE" className="bg-bg-primary">Male</option>
            <option value="FEMALE" className="bg-bg-primary">Female</option>
            <option value="OTHER" className="bg-bg-primary">Other</option>
          </select>
        </label>
        {/* Cricinfo ID — exact-match search. Ingest pipeline stamps
            Player.ci_player_id from the source athlete row so operators
            can look up any archive-linked profile by its Cricinfo ID
            without scrolling through the fuzzy name search. */}
        <label className="inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-bg-secondary border border-border-default text-text-secondary">
          <Fingerprint className="w-3.5 h-3.5" />
          <span>Cricinfo ID</span>
          <input
            type="text"
            value={ciPlayerIdInput}
            onChange={(e) => setCiPlayerIdInput(e.target.value)}
            placeholder="e.g. 253802"
            className="bg-transparent text-text-primary text-xs w-24 focus:outline-none placeholder:text-text-secondary/60"
          />
          {ciPlayerIdInput && (
            <button
              type="button"
              onClick={() => setCiPlayerIdInput("")}
              title="Clear Cricinfo ID filter"
              className="text-text-secondary hover:text-text-primary"
            >
              <X className="w-3 h-3" />
            </button>
          )}
        </label>
      </div>
      {showQuickAdd && (
        <QuickAddPlayersModal
          onClose={() => setShowQuickAdd(false)}
          onCreated={() => setQuickAddNonce((n) => n + 1)}
        />
      )}
    <ListTemplate
      key={`players-list-${quickAddNonce}`}
      title="Players"
      fetchFn={listPlayers}
      deleteFn={deletePlayer}
      getAddPath={() => "/cricket/players/add"}
      getEditPath={(id) => `/cricket/players/${id}/edit`}
      // Ingested players are SYSTEM + is_locked, so the default rule hid
      // Edit on exactly the rows the founder most often needs to correct
      // (misspelt names on an ingested squad). The Players API allows a
      // superuser write on those rows, so surface the button here.
      superuserEditsLocked
      bulkActions={isSuperuser ? (ids, reset, reload) => (
        <BulkDeleteAction
          ids={ids}
          deleteFn={deletePlayer}
          label="players"
          reset={reset}
          reload={reload}
        />
      ) : undefined}
      columns={[
        { key: "name",         label: "Player",   render: (r) => {
            const display = playerDisplayName(r);
            const formal = fullName(r);
            return (
              <a href={`/cricket/players/${r.id}/profile`} className="block hover:text-button-primary transition-colors">
                <div className="font-medium flex items-center gap-1.5 flex-wrap">
                  <span>{display}</span>
                  {r.ci_player_id ? (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-amber-500/15 text-amber-300 border border-amber-500/40"
                      title={`Ingested from a bulk player-profile upload — Cricinfo ID ${r.ci_player_id}`}
                    >
                      ING
                    </span>
                  ) : r.owner_type === "SYSTEM" && (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/40"
                      title="Curated SYSTEM-owned profile (not from a bulk ingest)"
                    >
                      SYS
                    </span>
                  )}
                  {r.linked_user && (
                    <span
                      className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/40"
                      title="Self-signed-up player profile"
                    >
                      SELF
                    </span>
                  )}
                </div>
                {formal && formal !== "—" && formal !== display && (
                  <div className="text-[11px] text-text-secondary">{formal}</div>
                )}
              </a>
            );
          }},
        { key: "role",         label: "Role",     render: (r) => <Pill value={r.role} tone={ROLE_TONE[r.role]} /> },
        { key: "batting_hand", label: "Bat",      render: (r) => <Pill value={r.batting_hand} tone={HAND_TONE[r.batting_hand]} /> },
        // Show every hand the player bowls with (ambidextrous = 2 pills).
        // Falls back to the legacy singular for records not yet resaved.
        { key: "bowling_hands", label: "Bowl",    render: (r) => {
            const hands = (Array.isArray(r.bowling_hands) && r.bowling_hands.length)
              ? r.bowling_hands
              : (r.bowling_hand ? [r.bowling_hand] : []);
            return (
              <div className="flex gap-1 flex-wrap">
                {hands.map((h) => <Pill key={h} value={h} tone={HAND_TONE[h]} />)}
              </div>
            );
          } },
        { key: "bowling_style",label: "Style",    render: (r) => <span className="text-text-secondary text-xs">{r.bowling_style || "—"}</span> },
        { key: "nationality",  label: "Country",  render: (r) => nameOf(r.nationality) },
        { key: "ci_player_id", label: "CI ID",    render: (r) => <span className="font-mono text-[11px] text-text-secondary">{r.ci_player_id || "—"}</span> },
      ]}
    />
    </>
  );
}
